import { useState, type FC, type KeyboardEvent } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useTranslation } from "@/shared/hooks";
import { shouldSubmitOnKey } from "../lib/shouldSubmitOnKey";

// Лимит реплики зрителя в чате помощника (не текста обращения в поддержку,
// SupportRequest.message: тот отдельно ограничен на бэкенде 2000 символами).
// Совпадает с ASSISTANT_LIMITS.viewerMessageMaxLength, роут отклоняет то же
// значение первым, до цикла помощника.
export const ASSISTANT_MESSAGE_MAX_LENGTH = 500;
const COUNTER_FROM = 400;

interface AssistantComposerProps {
  sending: boolean;
  onSend: (text: string) => void;
  /** Для тестов рендера. */
  initialValue?: string;
}

export const AssistantComposer: FC<AssistantComposerProps> = ({
  sending,
  onSend,
  initialValue = "",
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const text = value.trim();
  const canSend =
    !sending && text.length > 0 && text.length <= ASSISTANT_MESSAGE_MAX_LENGTH;

  const submit = () => {
    if (!canSend) return;
    onSend(text);
    setValue("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      shouldSubmitOnKey({
        key: event.key,
        shiftKey: event.shiftKey,
        isComposing: event.nativeEvent.isComposing,
      })
    ) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 1,
        p: 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("assistant.placeholder")}
          multiline
          maxRows={4}
          fullWidth
          size="small"
          // Composer монтируется заново при каждом открытии панели (Grow
          // unmountOnExit на десктопе, Dialog размонтирует по умолчанию на
          // телефоне), поэтому autoFocus на маунте фокусирует поле именно
          // при открытии, а не один раз при первом рендере приложения.
          autoFocus
          slotProps={{
            htmlInput: { maxLength: ASSISTANT_MESSAGE_MAX_LENGTH },
          }}
        />
        {value.length > COUNTER_FROM && (
          <Typography
            variant="caption"
            color={
              value.length >= ASSISTANT_MESSAGE_MAX_LENGTH
                ? "error"
                : "text.secondary"
            }
            sx={{ display: "block", textAlign: "right", mt: 0.5 }}
          >
            {value.length}/{ASSISTANT_MESSAGE_MAX_LENGTH}
          </Typography>
        )}
      </Box>
      <IconButton
        type="submit"
        color="primary"
        disabled={!canSend}
        aria-label={t("assistant.send")}
        sx={{ mb: 0.25 }}
      >
        <SendRoundedIcon />
      </IconButton>
    </Box>
  );
};
